<script>
import FilePicker from "./FilePicker.vue";
import FolderPicker from "./FolderPicker.vue";
import {uploadFileMixins} from "../../../mixins/fileUploadLegacy.js";
import {notificationMixins} from "../../../mixins/notification.js";
import {subjectMixins} from "../../../mixins/subject.js";
import {apiMixins} from "../../../mixins/api.js";
import {prettyPrintFileSize} from "@/util/file";
import Metadata from "../../common/Metadata.vue";
import RetentionSchedule from "./UploadRetentionSchedule.vue";
import { folderMixins } from "../../../mixins/folder.js";
import Popup from "../../common/Popup.vue";
import PropertiesSection from "../../common/PropertiesSection.vue";
import KeywordSelector from "../../common/KeywordSelector.vue";

const STATE = {
  FILE_PICK: 0,
  FILE_OPTIONS: 1,
  UPLOADING: 2,
  DONE: 3,
  CANCELLED: 4,
};

export default {
  mixins: [uploadFileMixins, notificationMixins, apiMixins, subjectMixins, folderMixins],
  components: {
    FilePicker,
    FolderPicker,
    Metadata,
    RetentionSchedule,
    Popup,
    PropertiesSection,
    KeywordSelector,
  },
  props: ["folderId"],
  emits: ["stateChanged"],
  data() {
    return {
      STATE,
      state: STATE.FILE_PICK,
      uploader: null,
      currentItem: 0,
      currentFilename: "",
      currentPhase: "",
      currentPhaseProgress: { current: 0, total: 0 },
      totalFolders: 0,
      foldersDone: 0,
      collectionFileSize: 0,
      chosenFiles: [],
      progress: 0,
      isSecret: false,
      selectedRetentionSchedule: null,
      currentSubjects: [],
      allSubjects: [],
      currentMetadata: [],
      metadataItem: { metadata: [] },
      folder: null,
      failedFiles: [],
      pendingFiles: [],
      zeroByteFiles: [],
      suppressUploadErrors: false,
      cancelRequested: false,
      pickerResetKey: 0,
    };
  },
  computed: {
    availableSubjects() {
      return this.allSubjects.filter(s => !this.currentSubjects.includes(s.label));
    }
  },
  asyncComputed: {
    retentionSchedules() {
      return this.callAPI("item/retention-schedules", {
        // loader MUST NOT be used here, as the async watching behaviour causes an infinite loop
        // See https://github.com/foxbenjaminfox/vue-async-computed/issues/108
        showLoader: false,
      });
    },
  },
  methods: {
    applyFilesSelection(files) {
      this.chosenFiles = files;
      this.setState(STATE.FILE_OPTIONS);
      let fileSizeSum = 0;
      this.chosenFiles.forEach(f => {
        fileSizeSum += parseInt(f.file.size);
      });
      this.collectionFileSize = fileSizeSum;
    },
    formatZeroByteUploadMessage(zeroByteFiles) {
      if (!zeroByteFiles.length) {
        return "";
      }
      return `${this.$t("zero-byte-file-size")}. ${this.$t("zero-byte-file-guidance")}`;
    },
    zeroByteFileLabel(item) {
      const name = item?.file?.name || "";
      const path = item?.path || "";
      if (!path) {
        return name;
      }
      if (!name || path.endsWith(name)) {
        return path;
      }
      return `${path}${name}`;
    },
    confirmZeroByteFiles() {
      this.applyFilesSelection(this.pendingFiles);
      this.pendingFiles = [];
      this.zeroByteFiles = [];
    },
    cancelZeroByteFiles() {
      this.pendingFiles = [];
      this.zeroByteFiles = [];
      this.chosenFiles = [];
      this.collectionFileSize = 0;
      this.pickerResetKey += 1;
      this.setState(STATE.FILE_PICK);
    },
    requestCancel() {
      this.cancelRequested = true;
    },
    clearCancelIntent() {
      this.cancelRequested = false;
    },
    filesReadyHandler(files) {
      if (!files.length) {
        return;
      }
      const zeroByteFiles = files.filter((item) => item.file.size === 0);
      if (zeroByteFiles.length > 0) {
        this.pendingFiles = files;
        this.zeroByteFiles = zeroByteFiles;
        return;
      }
      this.applyFilesSelection(files);
    },
    returnToBrowse() {
      if (this.$route.name === "browse" && String(this.$route.params.id) === String(this.folderId)) {
        return;
      }
      this.$router.push({name: "browse", params: {id: this.folderId}})
        .catch((err) => {
          if (err && err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
    },
    buildSensitiveUploadWarning(sensitiveDetections) {
      if (!Array.isArray(sensitiveDetections) || sensitiveDetections.length === 0) {
        return "";
      }
      const categoryLabel = Array.from(new Set(
        sensitiveDetections.flatMap((detection) => detection?.matchedCategories || [])
      )).join(", ");
      return this.$t("upload-sensitive-warning", {
        count: sensitiveDetections.length,
        categories: categoryLabel || this.$t("sensitive-data"),
      });
    },
    showUploadCompletionNotifications(sensitiveDetections) {
      this.showSuccess(this.$t("upload-success"));
      const sensitiveWarning = this.buildSensitiveUploadWarning(sensitiveDetections);
      if (sensitiveWarning) {
        this.showWarning(sensitiveWarning, { expiryTime: 12000 });
      }
    },
    async startUpload() {
      if (this.uploader || this.state === STATE.UPLOADING || !this.selectedRetentionSchedule) {
        return;
      }
      this.cancelRequested = false;
      this.suppressUploadErrors = false;
      this.setState(STATE.UPLOADING);
      this.uploader = this.uploadFiles(
          this.chosenFiles.map((item) => ({
            parentItem: {id: this.folderId},
            name: item.file.name,
            subPath: item.path,
            retentionSchedule: this.selectedRetentionSchedule.id,
            file: item.file,
            isSecret: this.isSecret,
            tags: this.currentSubjects,
            metadata: this.currentMetadata,
          })),
          (item, fileName, numberOfItems, phase, phaseProgress) => {
            this.currentItem = Math.floor(item);
            this.currentFilename = fileName;
            this.currentPhase = phase || "uploading-files";
            if (phaseProgress) {
              this.currentPhaseProgress = phaseProgress;
              this.totalFolders = phaseProgress.total || 0;
              this.foldersDone = phaseProgress.current || 0;
            }
            const totalItems = numberOfItems || 0;
            const totalWork = this.totalFolders + totalItems;
            if (totalWork === 0) {
              this.progress = 0;
              return;
            }
            if (this.currentPhase === "creating-folders") {
              this.progress = this.foldersDone / totalWork;
            } else {
              const filesDone = Math.min(item, totalItems);
              this.progress = (this.foldersDone + filesDone) / totalWork;
            }
          },
          (item, error) => {
            if (this.suppressUploadErrors || this.state === STATE.CANCELLED) {
              return;
            }
            const message = error?.userMessage || this.$t("upload-error", {filename: item.file.name});
            this.showError(message, {expiryTime: 30000});
            this.failedFiles.push(item);
          },
      );
      let success = false;
      let runResult = null;
      try {
        runResult = await this.uploader.run();
        const cancelled = !!runResult?.cancelled;
        const anySuccess = runResult?.result?.anySuccess;
        const sensitiveDetections = runResult?.result?.sensitiveDetections || [];
        success = !cancelled && anySuccess !== false;
        runResult = {
          ...runResult,
          result: {
            ...(runResult?.result || {}),
            sensitiveDetections,
          },
        };
      } catch (ex) {
        console.log(ex);
      }
      this.uploader = null;
      if (runResult?.cancelled || this.cancelRequested || this.state === STATE.CANCELLED) {
        return;
      }
      if (success) {
        this.setState(STATE.DONE);
        this.showUploadCompletionNotifications(runResult?.result?.sensitiveDetections || []);
        // Call the backend API to touch the folder hierarchy
        await this.touchFolder(this.folder.id);
        setTimeout(() => this.returnToBrowse(), 1000);
      } else {
        this.setState(STATE.FILE_OPTIONS);
      }
    },
    async cancelUpload(shouldNavigate = true) {
      if (this.uploader) {
        this.cancelRequested = true;
        this.suppressUploadErrors = true;
        this.setState(STATE.CANCELLED);
        this.uploader.cancel();
        this.showError(this.$t("upload-cancel"));
      }
      if (shouldNavigate) {
        this.returnToBrowse();
      }
    },
    setState(state) {
      this.state = state;
      this.$emit("stateChanged", state); 
    },
    tagAdded(tag) {
      if (tag.value && !this.currentSubjects.includes(tag.value)) {
        this.currentSubjects.push(tag.value);
      }
    },
    tagRemoved(tag) {
      this.currentSubjects = this.currentSubjects.filter(s => s !== tag);
    },
    tagsChanged(tags) {
      this.currentSubjects = tags;
    },
    metadataChanged(metadata) {
      this.currentMetadata = metadata;
    },
    onRetentionScheduleChangedHandler(selectedRetentionSchedule) {
      this.selectedRetentionSchedule = selectedRetentionSchedule;
    },
    async loadSubjects() {
      var response = await this.getAvailableSubjects(this.folderId, { showLoader: true });
      this.allSubjects = response.subjects;
    },
    async loadFolder() {
      this.getFolder(this.folderId, {
        page: 0,
        pageSize: 1,
        sort: "label,asc"
      })
      .then(response => {
        this.folder = response.self;
      });
    },
    clearFailedFiles() {
      this.failedFiles = [];
      this.setState(STATE.FILE_PICK);
    },
  },
  filters: {
    prettyBytes(value) {
      return prettyPrintFileSize(value);
    }
  },
  created() {
    this.loadSubjects();
    this.loadFolder();
  }
};
</script>

<template>
  <div class="upload">
    <FilePicker v-if="state === STATE.FILE_PICK" :key="`file-${pickerResetKey}`" @filesReady="filesReadyHandler"/>    
    <FolderPicker v-if="state === STATE.FILE_PICK" :key="`folder-${pickerResetKey}`" @filesReady="filesReadyHandler"/>

    <section class="options" v-if="state >= STATE.FILE_OPTIONS">
      <div>
        <h5 class="label">{{ $t("files-selected") }}</h5>
        <p class="value">{{ this.chosenFiles.length }}</p>
      </div>
      <div>
        <h5 class="label">{{ $t("total-size") }}</h5>
        <p class="value">{{ collectionFileSize | prettyBytes }}</p>
      </div>
    </section>

    <RetentionSchedule
      v-if="state >= STATE.FILE_OPTIONS"
      id="retention-schedule"
      :item="folder"
      @retention-schedule-changed="onRetentionScheduleChangedHandler"
    />
    <PropertiesSection :title="$t('confidentiality-heading')" v-if="state >= STATE.FILE_OPTIONS">
        <label class="confidentiality-label">
          <input type="checkbox" v-model="isSecret">
          {{ $t("confidentiality-label") }}
          <span
            class="help-icon"
            :title="$t('secret-item-tooltip')"
            :aria-label="$t('secret-item-tooltip')"
          >?</span>
        </label>
    </PropertiesSection>
    <PropertiesSection :title="$t('keywords-heading')" :title-help="$t('properties.keywords-help')" v-if="state >= STATE.FILE_OPTIONS">
      <KeywordSelector
        id="add-tag"
        :allSubjects="allSubjects"
        :selectedTags="currentSubjects"
        :description="$t('keywords-description')"
        alignActionsToPanel
        @tags-changed="tagsChanged"
      />
    </PropertiesSection>

      <Metadata
        :item="metadataItem"
        :metadata="currentMetadata"
        :title-help="$t('properties.metadata-help')"
        @metadata-changed="metadataChanged"
        v-if="state >= STATE.FILE_OPTIONS"
      />

    <section class="progress" v-if="state >= STATE.UPLOADING">
      <h5 class="label">
        <template v-if="currentPhase === 'creating-folders'">
          {{ $t("upload-creating-folders") }}
        </template>
        <template v-else>
          {{ $t("uploading", { item: this.currentFilename }) }}
        </template>
      </h5>
      <p v-if="state === STATE.DONE">{{ $t("upload-complete") }}</p>
      <p v-else-if="currentPhase === 'creating-folders'">
        {{ $t("upload-creating-folders-progress", {current: currentPhaseProgress.current, total: currentPhaseProgress.total}) }}
      </p>
      <p v-else>{{ $t("upload-progress", {current: currentItem + 1, total: this.chosenFiles.length}) }}</p>
      <m-linear-progress :buffer="progress > 0 ? 1 : 0" :open="true" :progress="progress"/>
    </section>

    <section class="buttons" v-if="state <= STATE.UPLOADING">
      <button class="primary" :disabled="!selectedRetentionSchedule || state === STATE.UPLOADING" @click="startUpload">
        {{ $t("begin-upload") }}
      </button>
      <button @click="$emit('confirmCancel')">
        {{ $t("cancel") }}
      </button>
    </section>

    <Popup 
      v-if="state === STATE.FILE_OPTIONS && failedFiles.length > 0" :defaultButtons="false"
      @cancelled="clearFailedFiles">
      <template v-slot:header>Failed Files</template>
      <template v-slot:body>
        <div class="failed-files-label">The following files failed to upload:</div>
        <div class="failed-files-block">
          <p v-for="failedFile in failedFiles" :key="failedFile.name">{{ failedFile.subPath }}{{failedFile.name}}</p>
        </div>
      </template>
      <template v-slot:footer>
        <div class="buttons">
        <button type="button" class="primary" @click.stop="clearFailedFiles">{{ $t("cancel") }}</button>
        </div>
      </template>
    </Popup>

    <Popup
      v-if="zeroByteFiles.length > 0"
      @cancelled="cancelZeroByteFiles"
      @confirmed="confirmZeroByteFiles"
      icon="warning">
      <template v-slot:header>{{ $t("zero-byte-file-title") }}</template>
      <template v-slot:body>
        <p class="zero-byte-upload-message">{{ formatZeroByteUploadMessage(zeroByteFiles) }}</p>
        <div class="failed-files-block">
          <p v-for="item in zeroByteFiles" :key="zeroByteFileLabel(item)">{{ zeroByteFileLabel(item) }}</p>
        </div>
      </template>
      <template v-slot:confirmLabel>{{ $t("zero-byte-file-confirm") }}</template>
    </Popup>


  </div>
</template>     

<style lang="scss" scoped>
.upload {
  padding-top: 1rem;
}

h5 {
  font-weight: bold;
  margin-bottom: 1rem;
}

.options {
  display: flex;
  background: white;
  border: 1px solid #C8D2DC;
  border-radius: 8px;
  box-sizing: border-box;
  font-size: 0.875rem;
  margin-bottom: 1rem;

  > div {
    padding: 1.5rem;

    &:not(:last-child) {
      border-right: 1px solid #E0E0E0;
    }
  }
}

.required-field:after {
  content:" *";
  color: red;
}

.confidentiality, .tags {
  > h5 {
    margin: 2rem 1rem 0;
  }

  > section {
    display: flex;
    flex-direction: column;
    margin: 1rem 0;
    background: white;
    padding: 1.5rem;

    label {
      margin-left: 1rem;
      font-weight: bold;
    }

    input {
      margin-right: 0.5rem;
    }
  }

  select {
    margin: 1rem 0
  }

  .warning {
    color: #EF0000;
  }
}

.help-icon {
  align-items: center;
  background: transparent;
  border: 1px solid #1275CC;
  border-radius: 50%;
  color: #1275CC;
  cursor: help;
  display: inline-flex;
  flex: 0 0 1.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  height: 1.25rem;
  justify-content: center;
  line-height: 1;
  margin-left: 0.35rem;
  padding: 0;
  width: 1.25rem;
}

.progress {
  padding: 1.5rem 1.5rem 2rem;
  background: white;
  font-size: 0.875rem;
  border-top: 1px solid #E0E0E0;
}

.buttons {
  margin-top: 1rem;
}

.mdc-select,
.mdc-text-field {
  background-color: white;
  padding-left: 0;
  border-radius: 0;
}

.mdc-select .mdc-select__native-control,
.mdc-select .mdc-floating-label {
  padding-left: 0.5rem;
}

input {
  background-color: #fff;
  padding-top: 0;
  padding-left: 0.1rem;
}

.relative-dates a.chip-trigger {
  display: block;
  padding: 0.5rem;
}

.relative-dates .mdc-chip {
  padding: 0;
}

.buttons button {
  margin-right: 1rem;
}

.buttons button {
  background: transparent;
  border: 1px solid #1275CC;
  color: #1275CC;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-weight: bold;
  cursor: pointer;
  line-height: 1.6rem;

  &:disabled {
    cursor: default;
    opacity: 0.2;
  }

  &.primary {
    background: #1275CC;
    border-color: transparent;
    color: white;
  }

  > i {
    vertical-align: -0.35rem;
    padding-right: 0.25rem;
  }
}

.failed-files-block {
  overflow: auto;
  height: 100px;
  border: 1px solid rgb(118, 118, 118);
  padding: 0.5rem;
}

.failed-files-label {
  margin-bottom: 0.5rem;
}

.zero-byte-upload-message {
  margin-bottom: 1.5rem;
}
</style>
